import { Component } from "@theme/component";
import { ThemeEvents, MediaStartedPlayingEvent } from "@theme/events";
import { DialogCloseEvent } from "@theme/dialog";

// SLEEPSAKE EDIT: IntersectionObserver reports a target that only touches the
// viewport's edge as `isIntersecting` with a ratio of 0. A slideshow slide
// parked just past its scroller's edge is exactly that, so the comfort page's
// second gallery video used to download (~1.4MB) and play behind the first
// slide on load. Autoplay media counts as in view only once this share of it
// is inside the viewport (the viewport plus rootMargin, for the load
// observer). Passing the share as the observers' threshold makes crossing it,
// either way, fire a callback; with the default threshold of 0, a target that
// first reports as edge-adjacent never reports again as it scrolls fully in.
const AUTOPLAY_VISIBLE_RATIO = 0.1;

// WebKit (every iPhone browser) briefly lays a carousel slide out as a
// zero-size box on the carousel's edge, and a zero-size box touching the edge
// reports as fully visible (ratio 1), so the slide must also show some area.
/** @param {IntersectionObserverEntry} entry */
const isAutoplayInView = (entry) =>
  entry.isIntersecting &&
  entry.intersectionRatio >= AUTOPLAY_VISIBLE_RATIO &&
  entry.intersectionRect.width > 0 &&
  entry.intersectionRect.height > 0;

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;

    // SLEEPSAKE EDIT: Autoplay videos skip the click-to-play coordination below
    // (start one ⇒ pause the others, pause on zoom-dialog close) — pausing an
    // autoplay video here would leave it frozen on its poster with nothing to
    // resume it. Instead their <video> stays inside the <template> until the
    // section nears the viewport (keeping megabytes of mp4 off the initial
    // load), and playback then follows visibility.
    if (this.hasAttribute("autoplay")) {
      this.#deferAutoplay();
      return;
    }

    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#loadObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
  }

  /** @type {IntersectionObserver | undefined} */
  #loadObserver;

  /** @type {IntersectionObserver | undefined} */
  #visibilityObserver;

  // SLEEPSAKE EDIT: hydrate autoplay media from the <template> shortly before it
  // scrolls into view, then keep play/pause in sync with visibility.
  #deferAutoplay() {
    if (this.querySelector("video, iframe")) {
      // Already rendered eagerly (posterless videos) — only manage playback.
      this.#observeVisibility();
      return;
    }

    this.#loadObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some(isAutoplayInView)) return;
        this.#loadObserver?.disconnect();
        this.#loadObserver = undefined;
        this.loadContent(false, false);
        this.#observeVisibility();
      },
      // Start fetching one-and-a-half viewports ahead so playback is seamless on
      // scroll. The margin is vertical only: a slide off to the side waits until
      // it is swiped into view.
      { rootMargin: "150% 0px", threshold: AUTOPLAY_VISIBLE_RATIO },
    );
    this.#loadObserver.observe(this);
  }

  #observeVisibility() {
    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (isAutoplayInView(entry)) {
            const video = this.querySelector("video");
            // A finished non-loop video stays on its last frame instead of replaying.
            if (video?.ended && !video.loop) continue;
            this.playMedia();
          } else {
            this.pauseMedia();
          }
        }
      },
      { threshold: AUTOPLAY_VISIBLE_RATIO },
    );
    this.#visibilityObserver.observe(this);
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove("hidden");
      const playIcon = toggleMediaButton.querySelector(".icon-play");
      if (playIcon) playIcon.classList.toggle("hidden", isPlaying);
      const pauseIcon = toggleMediaButton.querySelector(".icon-pause");
      if (pauseIcon) pauseIcon.classList.toggle("hidden", !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   * @param {boolean} [notify] - Whether to broadcast MediaStartedPlayingEvent, which pauses
   *   other click-to-play media. Deferred autoplay hydration passes false so scrolling past
   *   an autoplay section never pauses a video the user started.
   */
  loadContent(focus = true, notify = true) {
    if (this.getAttribute("data-media-loaded")) return;

    if (notify) this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector("template")?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute("data-media-loaded", "true");
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add("deferred-media__playing");

    if (content instanceof HTMLVideoElement && content.getAttribute("autoplay")) {
      // force autoplay for safari; swallow the AbortError when the visibility
      // observer's initial not-intersecting callback pauses a still-pending play()
      content.play()?.catch(() => {});
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector("iframe[data-video-type]");
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === "youtube"
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        "*",
      );
    } else {
      // SLEEPSAKE EDIT: swallow the AbortError raised when a visibility-driven pause
      // interrupts a still-pending play() during fast scrolling.
      this.querySelector("video")?.play()?.catch(() => {});
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector("iframe[data-video-type]");

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === "youtube"
          ? '{"event":"command","func":"' + "pauseVideo" + '","args":""}'
          : '{"method":"pause"}',
        "*",
      );
    } else {
      this.querySelector("video")?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute("data-media-loaded")) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get("deferred-media")) {
  customElements.define("deferred-media", DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: "model-viewer-ui",
        version: "1.0",
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector("model-viewer");
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      "pointerdown",
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal },
    );

    element.addEventListener(
      "click",
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal },
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get("product-model")) {
  customElements.define("product-model", ProductModel);
}
